import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { PublicShell } from '@/features/public/PublicShell';
import { ONLINE_BOOKING_ENABLED } from '@/config/featureFlags';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';

const page = <T extends Record<string, unknown>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() => loader().then((m) => ({ default: m[name] as React.ComponentType })));

// Public (unauthenticated) customer site
const LandingPage = page(() => import('@/features/public/LandingPage'), 'LandingPage');
const BookingPage = page(() => import('@/features/public/BookingPage'), 'BookingPage');
const BookingUnavailable = page(
  () => import('@/features/public/BookingUnavailable'),
  'BookingUnavailable',
);

// Operator console
const LoginPage = page(() => import('@/features/auth/pages/LoginPage'), 'LoginPage');
const SignUpPage = page(() => import('@/features/auth/pages/SignUpPage'), 'SignUpPage');
const ForgotPasswordPage = page(
  () => import('@/features/auth/pages/ForgotPasswordPage'),
  'ForgotPasswordPage',
);
const ResetPasswordPage = page(
  () => import('@/features/auth/pages/ResetPasswordPage'),
  'ResetPasswordPage',
);
const OverviewPage = page(() => import('@/features/overview/pages/OverviewPage'), 'OverviewPage');
const FloorPage = page(() => import('@/features/floor/pages/FloorPage'), 'FloorPage');
const MembershipPage = page(() => import('@/features/membership/pages/MembershipPage'), 'MembershipPage');
const BookingsPage = page(() => import('@/features/bookings/pages/BookingsPage'), 'BookingsPage');
const CustomersPage = page(() => import('@/features/customers/pages/CustomersPage'), 'CustomersPage');
const FoodPage = page(() => import('@/features/food/pages/FoodPage'), 'FoodPage');
const AttendancePage = page(() => import('@/features/attendance/pages/AttendancePage'), 'AttendancePage');
const MenuPage = page(() => import('@/features/menu/pages/MenuPage'), 'MenuPage');
const RatesPage = page(() => import('@/features/rates/pages/RatesPage'), 'RatesPage');
const GamesPage = page(() => import('@/features/games/pages/GamesPage'), 'GamesPage');
const PaymentsPage = page(() => import('@/features/payments/pages/PaymentsPage'), 'PaymentsPage');
const ExpensesPage = page(() => import('@/features/expenses/pages/ExpensesPage'), 'ExpensesPage');
const InsightsPage = page(() => import('@/features/insights/pages/InsightsPage'), 'InsightsPage');
const ReportsPage = page(() => import('@/features/reports/pages/ReportsPage'), 'ReportsPage');
const TeamPage = page(() => import('@/features/users/pages/TeamPage'), 'TeamPage');
const SettingsPage = page(() => import('@/features/settings/pages/SettingsPage'), 'SettingsPage');

function RequireAuth() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

/** The /app landing. Roles without the Overview go straight to the Live Floor. */
function AppIndex() {
  const { can } = useRole();
  if (!can('overview:view')) return <Navigate to="/app/floor" replace />;
  return <OverviewPage />;
}

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function PublicLayout() {
  return (
    <PublicShell>
      <Outlet />
    </PublicShell>
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-base text-sm text-muted">
      Loading…
    </div>
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public customer site */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/book"
            element={ONLINE_BOOKING_ENABLED ? <BookingPage /> : <BookingUnavailable />}
          />
        </Route>

        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Operator console (login required) */}
        <Route path="/app" element={<RequireAuth />}>
          <Route element={<ShellLayout />}>
            <Route index element={<AppIndex />} />
            <Route path="floor" element={<FloorPage />} />
            <Route path="bookings" element={<BookingsPage />} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="membership" element={<MembershipPage />} />
            <Route path="food" element={<FoodPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="menu" element={<MenuPage />} />
            <Route path="rates" element={<RatesPage />} />
            <Route path="games" element={<GamesPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route path="expenses" element={<ExpensesPage />} />
            <Route path="insights" element={<InsightsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
